import React, { useState, useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import {
  type NodeProps,
  type Node,
  Handle,
  Position,
  useReactFlow,
} from "@xyflow/react";
import {
  Repeat,
  Trash2,
  Settings,
  X,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Copy,
} from "lucide-react";
import syntaxHighlight from "../../utils/jsonViewer";
import { NodeDataSidebar } from "../../common/shared/NodeDataSidebar";
import { AvailableVariable } from "../../common/ui/types";
import { getAllAncestorNodes } from "../../utils/workflowHelpers";
import NodeDocsButton from "../../docs/NodeDocsButton";

/* ---------- Main component ---------- */

export const ForEachNode: React.FC<NodeProps<Node<any>>> = ({
  id,
  data,
  selected,
}) => {
  const [items, setItems] = useState<string>(
    (data?.items as string) || (data?.arrayInput as string) || ""
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setNodes, deleteElements, getNodes, getEdges } = useReactFlow();
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Get execution status from node data
  const executionStatus = data?.executionStatus || null;
  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const stopPropagation = useCallback(
    (e: React.MouseEvent | React.FocusEvent) => {
      e.stopPropagation();
    },
    []
  );

  const handleDeleteNode = useCallback(() => {
    deleteElements({ nodes: [{ id }] });
  }, [deleteElements, id]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDeleteNode();
  };

  const handleOpenModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const validationStatus = useMemo(() => {
    const hasInput = !!items;
    return hasInput
      ? { isValid: true, errors: [] }
      : {
          isValid: false,
          errors: ["Array input source is required."],
        };
  }, [items]);

  const statusConfig = useMemo(() => {
    if (isExecuting) {
      return {
        bg: "bg-blue-500",
        text: "Executing...",
        border: "border-blue-500",
        pulse: true,
      };
    } else if (executionSuccess) {
      return {
        bg: "bg-green-500",
        text: "Executed Successfully",
        border: "border-green-500",
        pulse: false,
      };
    } else if (executionError) {
      return {
        bg: "bg-red-500",
        text: "Execution Failed",
        border: "border-red-500",
        pulse: false,
      };
    }

    return validationStatus.isValid
      ? {
          bg: "bg-indigo-500",
          text: "Configured",
          border: "border-indigo-500",
          pulse: false,
        }
      : {
          bg: "bg-amber-500",
          text: "Not Configured",
          border: "border-amber-500",
          pulse: false,
        };
  }, [validationStatus, isExecuting, executionSuccess, executionError]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                items,
              },
            }
          : node
      )
    );
  }, [items, id, setNodes]);

  const extractNodeData = useCallback((nodeData: any) => {
    if (!nodeData || typeof nodeData !== "object") {
      return { inputs: {}, outputs: {} };
    }

    const outputs = nodeData.outputs || {};
    const inputs: Record<string, any> = {};
    Object.entries(nodeData).forEach(([key, value]) => {
      if (key !== "outputs" && key !== "executionStatus") inputs[key] = value;
    });

    return { inputs, outputs };
  }, []);

  const previousNodes = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();

    const ancestorNodes = getAllAncestorNodes(id, nodes, edges);
    return ancestorNodes.map((sourceNode) => {
      const { inputs, outputs } = extractNodeData(sourceNode.data);
      return {
        nodeId: sourceNode.id,
        nodeName: sourceNode.data?.name || sourceNode.id,
        nodeType: sourceNode.type || "unknown",
        inputs,
        outputs,
      };
    }) as AvailableVariable[];
  }, [id, getNodes, getEdges, isModalOpen, extractNodeData]);

  const handleDragStart = (
    e: React.DragEvent,
    variable: string,
    nodeName: string,
    type: "input" | "output"
  ) => {
    e.stopPropagation();
    const fullVariable =
      type === "output"
        ? variable
          ? `${nodeName}.outputs.${variable}`
          : `${nodeName}.outputs`
        : variable
        ? `${nodeName}.inputs.${variable}`
        : `${nodeName}.inputs`;
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("text/plain", fullVariable);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const variable = e.dataTransfer.getData("text/plain");
    if (variable) {
      setItems(variable);
    }
    setIsDraggingOver(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleSaveConfiguration = () => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? {
              ...node,
              data: {
                ...node.data,
                items,
                batch_size: 1,
                is_parallel: true,
              },
            }
          : node
      )
    );
    setIsModalOpen(false);
  };

  const hasOutputs = data.outputs && Object.keys(data.outputs).length > 0;

  return (
    <>
      <div
        className={`relative bg-white border-2 rounded-xl p-4 min-w-[280px] max-w-[300px] shadow-lg transition-all ${
          !validationStatus.isValid
            ? "border-red-400 shadow-red-100"
            : isExecuting
            ? "border-blue-500 shadow-lg shadow-blue-200 animate-pulse"
            : executionSuccess
            ? "border-green-500 shadow-md shadow-green-200"
            : executionError
            ? "border-red-500 shadow-md shadow-red-200"
            : selected
            ? "border-indigo-600 shadow-indigo-200"
            : "border-gray-200 hover:border-indigo-300 hover:shadow-xl"
        }`}
      >
        {/* Execution Overlay Spinner */}
        {isExecuting && (
          <div className="absolute inset-0 bg-blue-500/10 backdrop-blur-[1px] rounded-xl flex items-center justify-center z-10">
            <div className="bg-white rounded-full p-3 shadow-lg">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          </div>
        )}

         {/* Success Overlay */}
              {executionSuccess && (
                <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                  <div className="bg-green-500 rounded-full p-1.5 border-2 border-white">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
        
              {/* Error Overlay */}
              {executionError && (
                <div className="absolute -top-3 -right-4 z-30 pointer-events-none">
                  <div className="bg-red-500 rounded-full p-1.5 border-2 border-white">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
              
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-indigo-500 border-2 border-white"
          style={{
            boxShadow:
              "0 0 0 4px rgba(99, 102, 241, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
          }}
        />

        <div className="flex items-start justify-between mb-4 cursor-move">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg shadow-md transition-all ${
                isExecuting
                  ? "bg-blue-100 animate-pulse"
                  : executionSuccess
                  ? "bg-green-100"
                  : executionError
                  ? "bg-red-100"
                  : "bg-gradient-to-br from-indigo-500 to-indigo-600"
              }`}
            >
              {isExecuting ? (
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              ) : (
                <Repeat
                  className={`w-5 h-5 ${
                    executionSuccess
                      ? "text-green-700"
                      : executionError
                      ? "text-red-700"
                      : "text-white"
                  }`}
                />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-base">
                For Each
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <div
                  className={`w-2 h-2 rounded-full ${
                    statusConfig.bg
                  } shadow-sm ${statusConfig.pulse ? "animate-pulse" : ""}`}
                />
                <p className="text-xs font-medium text-gray-600">
                  {statusConfig.text}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NodeDocsButton nodeType="FOR-EACH" />
            <button
              onClick={handleDeleteClick}
              className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-all"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {!validationStatus.isValid && (
          <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded-lg text-xs">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 mb-1">
                  Configuration Required
                </p>
                <ul className="text-amber-700 space-y-1">
                  {validationStatus.errors.map((error, idx) => (
                    <li key={idx}>• {error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div onClick={stopPropagation}>
          <button
            onClick={handleOpenModal}
            className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-sm font-semibold flex justify-center items-center gap-2 shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-indigo-800 transition-all"
          >
            <Settings className="w-4 h-4" />
            Configure Loop
          </button>
        </div>

        {/* Loop Output Handle */}
        <div className="absolute right-0 top-1/3 transform translate-x-1/2 -translate-y-1/2 flex items-center">
          <span className="absolute text-[12px] font-semibold text-indigo-600 bg-white px-1 mr-1">
            Loop
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="loop"
            className="w-3 h-3 bg-indigo-500 border-2 border-white !bg-indigo-500"
            style={{
              top: 0,
              boxShadow:
                "0 0 0 4px rgba(99, 102, 241, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
            }}
          />
        </div>

        {/* Done Output Handle */}
        <div className="absolute right-0 top-2/3 transform translate-x-1/2 -translate-y-1/2 flex items-center">
          <span className="absolute text-[12px] font-semibold text-green-600 bg-white px-1 mr-1">
            Done
          </span>
          <Handle
            type="source"
            position={Position.Right}
            id="done"
            className="w-3 h-3 bg-green-500 border-2 border-white !bg-green-500"
            style={{
              top: 0,
              boxShadow:
                "0 0 0 4px rgba(34, 197, 94, 0.25), 0 6px 12px -2px rgba(0, 0, 0, 0.2)",
            }}
          />
        </div>
      </div>

      {isModalOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4"
            onClick={handleCloseModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-[95vw] h-[90vh] overflow-hidden flex flex-col"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded">
                    <Repeat className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Configure For Each Loop
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Iterate over a list of items from a previous node.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Three-column Layout */}
              <div className="flex-1 overflow-hidden flex divide-x divide-gray-200">
                {/* Left Sidebar - Previous Nodes */}
                <div className="w-[340px] shrink-0 overflow-hidden">
                  <NodeDataSidebar
                    availableVariables={previousNodes}
                    onDragStart={handleDragStart}
                  />
                </div>

                {/* Center - Main Configuration */}
                <div className="flex-1 overflow-y-auto bg-white p-6">
                  <div className="max-w-2xl mx-auto space-y-6">
                    {/* Input Array Selection */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        Array to Iterate Over{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <p className="text-xs text-gray-500 mb-3">
                        Drag an array output from the left sidebar into the box
                        below.
                      </p>
                      <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        className={`min-h-[100px] w-full p-4 border-2 border-dashed rounded-xl transition-all flex items-center justify-center gap-3 ${
                          isDraggingOver
                            ? "border-indigo-400 bg-indigo-50"
                            : items
                            ? "border-indigo-300 bg-indigo-50/50"
                            : "border-gray-300 bg-gray-50 hover:border-indigo-300"
                        }`}
                      >
                        {items ? (
                          <div className="flex items-start justify-between gap-3 w-full">
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-gray-500 mb-1">
                                Selected field
                              </div>
                              <code className="text-sm text-indigo-700 font-mono break-all">
                                {items}
                              </code>
                            </div>
                            <button
                              onClick={() => setItems("")}
                              className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded transition-colors shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 flex flex-col items-center justify-center py-6 text-gray-400 gap-2">
                            <Copy className="w-8 h-8 opacity-50" />
                            <span className="text-sm font-medium">
                              Drag array variable here
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Sidebar - Outputs */}
                <div className="w-80 bg-gray-50 overflow-y-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Node Outputs
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Available after execution.
                  </p>

                  {hasOutputs ? (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        JSON Output
                      </h4>
                      <div className="bg-white border border-gray-200 rounded-lg p-3 font-mono text-xs overflow-x-auto">
                        <pre className="whitespace-pre-wrap break-words">
                          <code
                            dangerouslySetInnerHTML={{
                              __html: syntaxHighlight(data.outputs),
                            }}
                          />
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-white border border-gray-200 rounded text-center mt-4">
                      <p className="text-xs text-gray-500 font-medium">
                        No outputs yet
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Execute the node to see results.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center gap-2">
                  {validationStatus.isValid ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="font-medium">Configuration Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-red-700">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">
                        {validationStatus.errors.length} issue(s) found
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveConfiguration}
                    disabled={!validationStatus.isValid}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ForEachNode;
